import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  surveillance,
  type RestrictedUser,
  type SurveillanceAlert,
} from "@/api/surveillance";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * Trade-surveillance admin — alert queue + restricted-users register.
 *
 * Detectors run every 5 min via SurveillanceDaemon and persist hits
 * here. Compliance officer triages each case to cleared / reviewing /
 * escalated. Restricted users (employees, PEPs, board-related parties)
 * are managed in a side panel; the front-running detector reads from
 * this list.
 */
export function AdminSurveillancePage() {
  const [tab, setTab] = useState<"alerts" | "restricted">("alerts");
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Trade surveillance</h1>
        <div className="flex gap-1">
          <Button
            variant={tab === "alerts" ? "primary" : "ghost"}
            onClick={() => setTab("alerts")}
          >
            Alerts
          </Button>
          <Button
            variant={tab === "restricted" ? "primary" : "ghost"}
            onClick={() => setTab("restricted")}
          >
            Restricted users
          </Button>
        </div>
      </div>
      {tab === "alerts" ? <AlertsTab /> : <RestrictedTab />}
    </div>
  );
}

function AlertsTab() {
  const [status, setStatus] = useState<SurveillanceAlert["status"] | "">("open");
  const [detector, setDetector] = useState<string>("");
  const [decideTarget, setDecideTarget] = useState<SurveillanceAlert | null>(null);

  const list = useQuery({
    queryKey: ["admin-surveillance", status, detector],
    queryFn: () =>
      surveillance.list({
        status: status || undefined,
        detector: detector || undefined,
      }),
    refetchInterval: 30_000,
  });

  return (
    <>
      <div className="flex justify-end gap-2">
        <Select
          className="w-40"
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
        >
          <option value="open">Open</option>
          <option value="reviewing">Reviewing</option>
          <option value="cleared">Cleared</option>
          <option value="escalated">Escalated</option>
          <option value="">All</option>
        </Select>
        <Select
          className="w-44"
          value={detector}
          onChange={(e) => setDetector(e.target.value)}
        >
          <option value="">All detectors</option>
          <option value="wash_trade">Wash trade</option>
          <option value="layering">Layering</option>
          <option value="spoofing">Spoofing</option>
          <option value="marking_close">Marking close</option>
          <option value="front_running">Front running</option>
        </Select>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No alerts"
              description="Detectors run every 5 minutes. New cases appear here automatically."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">When</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Detector</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">User</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Severity</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Status</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Detail</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((a) => (
                  <tr
                    key={a.id}
                    className="border-t border-term-border align-top hover:bg-term-elev"
                  >
                    <td className="px-3 py-1.5 text-term-dim">
                      {new Date(a.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-term-fg">
                      {a.detector_id}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="text-term-fg">{a.user_email ?? "—"}</div>
                      {a.user_name && (
                        <div className="text-2xs text-term-dim">{a.user_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-1.5">{severityBadge(a.severity)}</td>
                    <td className="px-3 py-1.5">{statusBadge(a.status)}</td>
                    <td className="max-w-md px-3 py-1.5">
                      <pre className="whitespace-pre-wrap break-words font-mono text-2xs text-term-dim">
                        {Object.keys(a.payload).length
                          ? JSON.stringify(a.payload, null, 0)
                          : "—"}
                      </pre>
                      {a.evidence_fill_ids.length > 0 && (
                        <div className="mt-1 text-2xs text-term-mute">
                          {a.evidence_fill_ids.length} fill(s),{" "}
                          {a.evidence_order_ids.length} order(s)
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {(a.status === "open" || a.status === "reviewing") && (
                        <Button onClick={() => setDecideTarget(a)}>Decide</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {decideTarget && (
        <DecideModal alert={decideTarget} onClose={() => setDecideTarget(null)} />
      )}
    </>
  );
}

function severityBadge(s: SurveillanceAlert["severity"]) {
  const tone =
    s === "critical" || s === "high" ? "bad" : s === "medium" ? "warn" : "neutral";
  return <Badge tone={tone as "bad" | "warn" | "neutral"}>{s}</Badge>;
}

function statusBadge(s: SurveillanceAlert["status"]) {
  const tone =
    s === "cleared"
      ? "good"
      : s === "escalated"
      ? "bad"
      : s === "reviewing"
      ? "warn"
      : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}

function DecideModal({
  alert,
  onClose,
}: {
  alert: SurveillanceAlert;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<SurveillanceAlert["status"]>("cleared");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: () =>
      surveillance.decide(alert.id, { status, note: note || undefined }),
    onSuccess: () => {
      toast.push("Decision recorded", "info");
      qc.invalidateQueries({ queryKey: ["admin-surveillance"] });
      onClose();
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title={`Decide · ${alert.detector_id}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          decide.mutate();
        }}
        className="space-y-3"
      >
        <p className="text-2xs text-term-dim">
          Detector <span className="font-mono">{alert.detector_id}</span>{" "}
          flagged user <span className="font-mono">{alert.user_email}</span> at{" "}
          {alert.severity} severity.
        </p>
        <pre className="overflow-auto rounded bg-term-elev p-2 font-mono text-2xs text-term-dim">
          {JSON.stringify(alert.payload, null, 2)}
        </pre>
        <div>
          <Label>Decision</Label>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as SurveillanceAlert["status"])}
          >
            <option value="cleared">cleared — false positive</option>
            <option value="reviewing">reviewing — hold while gathering info</option>
            <option value="escalated">escalated — refer for STR / regulator</option>
          </Select>
        </div>
        <div>
          <Label>Note (audit-logged)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={decide.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RestrictedTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const [openAdd, setOpenAdd] = useState(false);
  const list = useQuery({
    queryKey: ["surveillance-restricted"],
    queryFn: () => surveillance.restrictedList(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => surveillance.restrictedRemove(id),
    onSuccess: () => {
      toast.push("Restriction removed", "warn");
      qc.invalidateQueries({ queryKey: ["surveillance-restricted"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <>
      <div className="flex justify-end">
        <Button onClick={() => setOpenAdd(true)}>Add restriction</Button>
      </div>
      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No restricted users"
              description="Add employees, PEPs, or board-related parties whose trades should be scrutinized harder. The front-running detector treats their fills with extra scrutiny."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">User</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Category</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Note</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Added</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Status</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((r: RestrictedUser) => (
                  <tr key={r.id} className="border-t border-term-border">
                    <td className="px-3 py-1.5 text-term-fg">{r.user_email}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone="warn">{r.category}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-term-dim">{r.note ?? "—"}</td>
                    <td className="px-3 py-1.5 text-term-dim">
                      {new Date(r.added_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-1.5">
                      {r.removed_at ? (
                        <Badge>removed</Badge>
                      ) : (
                        <Badge tone="good">active</Badge>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {!r.removed_at && (
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm("Remove this restriction?")) remove.mutate(r.id);
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      {openAdd && <AddRestrictionModal onClose={() => setOpenAdd(false)} />}
    </>
  );
}

function AddRestrictionModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [userId, setUserId] = useState("");
  const [category, setCategory] = useState<RestrictedUser["category"]>("employee");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      surveillance.restrictedAdd({
        user_id: userId,
        category,
        note: note || undefined,
      }),
    onSuccess: () => {
      toast.push("Restriction added", "success");
      qc.invalidateQueries({ queryKey: ["surveillance-restricted"] });
      onClose();
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title="Add restricted user">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          add.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>User ID (UUID)</Label>
          <Input value={userId} onChange={(e) => setUserId(e.target.value)} required />
          <p className="mt-1 text-2xs text-term-dim">
            Look up the user's UUID from the Users page.
          </p>
        </div>
        <div>
          <Label>Category</Label>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as RestrictedUser["category"])}
          >
            <option value="employee">employee</option>
            <option value="pep">pep — politically exposed person</option>
            <option value="board_related">board_related</option>
            <option value="other">other</option>
          </Select>
        </div>
        <div>
          <Label>Note</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={add.isPending}>
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
}
